import * as Print from "expo-print";
import { useLocalSearchParams } from "expo-router";
import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/FontAwesome";
import { useDispatch, useSelector } from "react-redux";
import kanitFont from "../api/font_kanit.json";
import logovenine_base64 from "../api/logovenine_base64.json";
import FooterComponent from "../component/footer";
import { postOrderPrintlabel } from "../redux/actions/scaleactions";
import { useLanguage } from "./LanguageProvider";

type JobStatus =
  "" | "rawMaterial" | "inProduction" | "finishedProduct" | "ncr" | "other";

type PrintResponse = Record<string, any>;

const jobStatuses: Array<{ key: Exclude<JobStatus, "">; label: string }> = [
  { key: "rawMaterial", label: "วัตถุดิบ" },
  { key: "inProduction", label: "งานระหว่างผลิต" },
  { key: "finishedProduct", label: "สินค้าสำเร็จรูป" },
  { key: "ncr", label: "NCR" },
  { key: "other", label: "อื่นๆ" },
];

const parseItemParam = (
  itemParams: string | string[] | undefined,
): PrintResponse => {
  const rawValue = Array.isArray(itemParams) ? itemParams[0] : itemParams;

  if (!rawValue) return {};

  try {
    return JSON.parse(rawValue) as PrintResponse;
  } catch (error) {
    console.error("ไม่สามารถอ่านข้อมูล Print Order ได้:", error);
    return {};
  }
};

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== "";

const getPrintProxyBaseUrl = (): string => {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    return window.location.origin.replace(/\/+$/, "");
  }

  const configuredUrl = process.env.EXPO_PUBLIC_PRINT_PROXY_BASE_URL?.trim();

  // 🛠️ ลบการ throw Error ออก แล้วคืนค่าว่างถ้าหาไม่เจอ เพื่อไม่ให้แอปพัง
  return configuredUrl ? configuredUrl.replace(/\/+$/, "") : "";
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("อ่านรูปภาพไม่สำเร็จ"));
    reader.readAsDataURL(blob);
  });

const inlineWebPrintImages = async (htmlContent: string): Promise<string> => {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return htmlContent;
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(htmlContent, "text/html");
  const images = Array.from(parsedDocument.images);

  await Promise.all(
    images.map(async (image) => {
      const source = image.getAttribute("src") ?? "";

      if (!source || source.startsWith("data:")) return;

      try {
        const absoluteUrl = new URL(source, window.location.origin).toString();
        const response = await fetch(absoluteUrl, {
          method: "GET",
          cache: "no-store",
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`โหลดรูปไม่สำเร็จ: ${response.status}`);
        }

        image.src = await blobToDataUrl(await response.blob());
        image.removeAttribute("onerror");
      } catch (error) {
        console.warn("ไม่สามารถฝังรูปสำหรับพิมพ์ได้:", source, error);
      }
    }),
  );

  return `<!DOCTYPE html>\n${parsedDocument.documentElement.outerHTML}`;
};

const waitForWebPrintResources = async (
  printDocument: Document,
): Promise<void> => {
  const fontSet = (printDocument as Document & {
    fonts?: FontFaceSet;
  }).fonts;

  if (fontSet?.ready) {
    await Promise.race([
      fontSet.ready,
      new Promise((resolve) => window.setTimeout(resolve, 500)),
    ]);
  }

  const images = Array.from(printDocument.images);

  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete && image.naturalWidth > 0) {
            resolve();
            return;
          }

          let completed = false;
          const finish = () => {
            if (completed) return;
            completed = true;
            resolve();
          };

          image.addEventListener("load", finish, { once: true });
          image.addEventListener("error", finish, { once: true });
          window.setTimeout(finish, 1000);
        }),
    ),
  );
};

const addWebPrintToolbar = (
  printWindow: Window,
  printDocument: Document,
) => {
  const style = printDocument.createElement("style");
  style.textContent = `
    #web-print-toolbar {
      position: fixed;
      z-index: 999999;
      top: 10px;
      right: 10px;
      display: flex;
      gap: 8px;
      padding: 8px;
      border-radius: 10px;
      background: rgba(255,255,255,.96);
      box-shadow: 0 2px 12px rgba(0,0,0,.2);
      font-family: sans-serif;
    }
    #web-print-toolbar button {
      border: 0;
      border-radius: 8px;
      padding: 9px 14px;
      cursor: pointer;
      font-size: 14px;
    }
    #web-print-toolbar .print-button {
      color: #fff;
      background: #F17736;
    }
    #web-print-toolbar .close-button {
      color: #333;
      background: #e5e7eb;
    }
    @media print {
      #web-print-toolbar { display: none !important; }
    }
  `;
  printDocument.head.appendChild(style);

  const toolbar = printDocument.createElement("div");
  toolbar.id = "web-print-toolbar";

  const printButton = printDocument.createElement("button");
  printButton.type = "button";
  printButton.className = "print-button";
  printButton.textContent = "พิมพ์อีกครั้ง";
  printButton.onclick = () => {
    printWindow.focus();
    printWindow.print();
  };

  const closeButton = printDocument.createElement("button");
  closeButton.type = "button";
  closeButton.className = "close-button";
  closeButton.textContent = "ปิด";
  closeButton.onclick = () => printWindow.close();

  toolbar.append(printButton, closeButton);
  printDocument.body.appendChild(toolbar);
};

const printHtmlOnWeb = async (htmlContent: string): Promise<void> => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("Browser print is unavailable");
  }

  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    throw new Error(
      "Browser บล็อกหน้าต่างพิมพ์ กรุณาอนุญาต Pop-up สำหรับเว็บไซต์นี้",
    );
  }

  printWindow.document.open();
  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="th">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>กำลังเตรียมใบชี้บ่ง</title>
        <style>
          html, body {
            width: 100%;
            min-height: 100%;
            margin: 0;
            background: #fff;
            font-family: sans-serif;
          }
          body {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        </style>
      </head>
      <body>กำลังเตรียมเอกสารสำหรับพิมพ์...</body>
    </html>
  `);
  printWindow.document.close();

  let blobUrl = "";

  try {
    const finalHtml = await inlineWebPrintImages(htmlContent);
    const htmlBlob = new Blob([finalHtml], {
      type: "text/html;charset=utf-8",
    });

    blobUrl = URL.createObjectURL(htmlBlob);

    const loadPromise = new Promise<void>((resolve) => {
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };

      printWindow.addEventListener("load", finish, { once: true });
      window.setTimeout(finish, 1500);
    });

    printWindow.location.replace(blobUrl);
    await loadPromise;

    const printDocument = printWindow.document;
    await waitForWebPrintResources(printDocument);
    addWebPrintToolbar(printWindow, printDocument);

    await new Promise<void>((resolve) => {
      printWindow.requestAnimationFrame(() => {
        printWindow.requestAnimationFrame(() => resolve());
      });
    });
    await new Promise((resolve) => window.setTimeout(resolve, 50));

    printWindow.focus();
    printWindow.print();

    printWindow.addEventListener(
      "beforeunload",
      () => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      },
      { once: true },
    );

    window.setTimeout(() => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }, 10 * 60 * 1000);
  } catch (error) {
    if (blobUrl) URL.revokeObjectURL(blobUrl);

    try {
      printWindow.document.body.innerHTML = `
        <div style="padding:24px;font-family:sans-serif;color:#b91c1c">
          ไม่สามารถเตรียมเอกสารสำหรับพิมพ์ได้<br />
          กรุณาปิดหน้านี้แล้วลองใหม่
        </div>
      `;
    } catch {}

    throw error;
  }
};

export default function PrintScreen() {
  const { t } = useLanguage();
  const dispatch = useDispatch();
  const { item: itemParams } = useLocalSearchParams<{
    item?: string | string[];
  }>();

  const response = useMemo(() => parseItemParam(itemParams), [itemParams]);

  const profile =
    useSelector((state: any) => state?.auth?.profile?.user?.[0]) || null;

  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<JobStatus>("");
  const [modalQuestionVisible, setModalQuestionVisible] = useState(false);
  const [modalSuccessVisible, setModalSuccessVisible] = useState(false);
  const [modalErrorVisible, setModalErrorVisible] = useState(false);
  const [modalErrorcheck, setModalErrorcheck] = useState(false);
  const [modalPrintcatch, setModalPrintcatch] = useState(false);

  const base64Image = logovenine_base64?.base64;
  const kanitFontBase64 = kanitFont?.kanit;
  const headerText = String(response?.headerText ?? "");

  const handleSubmit = async () => {
    try {
      const result = await dispatch(
        postOrderPrintlabel(profile, response) as any,
      );

      if (result?.success === true) {
        setModalSuccessVisible(true);
      } else {
        console.error("บันทึกข้อมูลการพิมพ์ไม่สำเร็จ:", result?.message);
        setModalErrorVisible(true);
      }
    } catch (error) {
      console.error("เกิดข้อผิดพลาดขณะบันทึกข้อมูลการพิมพ์:", error);
      setModalErrorVisible(true);
    }
  };

  const generateHtmlContent = () => {
    const proxyBaseUrl = getPrintProxyBaseUrl();
    const qrValue = String(response?.qrCode ?? "");

    // 🛠️ กำหนด Link สำรองสำหรับ QR Code
    const directQrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrValue)}`;
    const qrCodeUrl =
      Platform.OS === "web" && proxyBaseUrl
        ? `${proxyBaseUrl}/proxy-qr/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrValue)}`
        : directQrCodeUrl;

    const createBarcodeHtml = (poValue: unknown, label: string) => {
      if (!hasValue(poValue)) return "";

      const poText = String(poValue);
      // 🛠️ กำหนด Link สำรองสำหรับ Barcode
      const directUrl = `https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(poText)}&scale=1&height=10&includetext=false`;
      const proxyUrl =
        Platform.OS === "web" && proxyBaseUrl
          ? `${proxyBaseUrl}/proxy-barcode/?bcid=code128&text=${encodeURIComponent(poText)}&scale=1&height=10&includetext=false`
          : directUrl;

      return `
        <div class="border-sig">
          <img 
            src="${escapeHtml(proxyUrl)}" 
            onerror="this.onerror=null;this.src='${escapeHtml(directUrl)}';" 
            alt="${escapeHtml(label)}" 
            class="barcode-image" 
          />
          <div class="bold">${escapeHtml(label)} ${escapeHtml(poText)}</div>
        </div>
      `;
    };

    const lengthText =
      response?.unit === "M"
        ? `${response?.quantity ?? ""} ${response?.unit ?? ""}`
        : response?.unit === "ROL" || response?.unit === "DR"
          ? `${response?.barcode ?? ""} M`
          : "";

    const quantityText =
      response?.unit === "DR"
        ? "1 DR"
        : response?.unit === "M" || response?.unit === "ROL"
          ? `${response?.quantity ?? ""} ${response?.unit ?? ""}`
          : hasValue(response?.returnWeight)
            ? `${response.returnWeight} KG`
            : "";

    const totalWeightText = hasValue(response?.totalWeight)
      ? `${response.totalWeight} KG`
      : "";

    const packagingWeightText = hasValue(response?.packagingWt)
      ? `${response.packagingWt} KG`
      : "";

    const materialWeightText = hasValue(response?.materialWt)
      ? `${response.materialWt} KG`
      : "";

    return `
<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    @page {
      size: 210mm 148mm;
      margin: 0;
    }

    @font-face {
      font-family: "Kanit";
      src: url("data:application/font-woff2;base64,${kanitFontBase64}") format("woff2");
      font-weight: normal;
      font-style: normal;
    }

    * {
      box-sizing: border-box;
    }

    html,
    body {
      width: 210mm !important;
      height: 148mm !important;
      min-width: 210mm;
      min-height: 148mm;
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff;
      overflow: hidden !important;
    }

    body {
      font-family: "Kanit", sans-serif;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .container {
      width: 206mm;
      height: 144mm;
      margin: 2mm;
      padding: 1.2mm 1.8mm;
      border: 0.45mm solid #000;
      display: flex;
      flex-direction: column;
      gap: 0.7mm;
      overflow: hidden;
      page-break-inside: avoid;
      break-inside: avoid-page;
    }

    .header {
      flex: 0 0 24mm;
      min-height: 24mm;
      display: flex;
      justify-content: space-between;
      align-items: center;
      overflow: hidden;
    }

    .header-left,
    .header-right {
      width: 30%;
    }

    .header-left {
      padding-left: 1mm;
      text-align: left;
    }

    .header-center {
      position: static;
      width: 40%;
      text-align: center;
      font-size: 10.5px;
      line-height: 1.12;
    }

    .header-right {
      padding-right: 1mm;
      text-align: right;
      font-size: 7.5px;
      line-height: 1.08;
    }

    .logo-image,
    .header-left img {
      display: block;
      width: auto;
      max-width: 40mm;
      height: 17mm;
      object-fit: contain;
    }

    .img-qr,
    .header .img_qr {
      display: inline-block;
      width: 21mm !important;
      height: 21mm !important;
      padding: 0;
      object-fit: contain;
    }

    .title {
      margin: 0;
      text-align: center;
      font-size: 16px;
      line-height: 1.15;
      font-weight: bold;
    }

    .main-table,
    .container > table {
      position: static;
      flex: 0 0 92mm;
      width: 100%;
      height: 92mm;
      border-collapse: collapse;
      table-layout: fixed;
      page-break-inside: avoid;
      break-inside: avoid-page;
    }

    .main-table > tbody,
    .container > table > tbody {
      height: 100%;
    }

    .main-table > tbody > tr,
    .container > table > tbody > tr {
      height: 8.3mm;
    }

    .main-table td,
    .main-table th,
    .container > table > tbody > tr > td,
    .container > table > tr > td {
      border: 0;
      padding: 0.55mm 0.9mm;
      font-size: 11.4px;
      line-height: 1.16;
      vertical-align: middle;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    /*
     * กำหนดความกว้างคอลัมน์ด้วย colgroup โดยตรง
     * เพื่อไม่ให้แถวที่มี colspan ดันคอลัมน์ข้อมูลด้านซ้ายไปอยู่กลางหน้า
     */
    .main-table col.col-left-label {
      width: 22%;
    }

    .main-table col.col-left-value {
      width: 28%;
    }

    .main-table col.col-right-label {
      width: 20%;
    }

    .main-table col.col-right-value {
      width: 30%;
    }

    /* ขยับค่าฝั่งซ้ายออกจากหัวข้อประมาณ 10mm */
    .main-table td.left-value {
      padding-left: 1mm;
    }

    .status-table,
    .table-layout table {
      position: static !important;
      width: 100%;
      height: auto !important;
      border-collapse: collapse;
      table-layout: fixed;
      text-align: center;
    }

    .status-table td,
    .table-layout table td {
      height: auto !important;
      padding: 0.2mm 0.25mm !important;
      font-size: 10.2px !important;
      line-height: 1.08;
      vertical-align: middle;
      white-space: nowrap;
    }

    .table-layout {
      position: static;
      text-align: center;
    }

    .signature {
      flex: 0 0 9mm;
      min-height: 9mm;
      margin: 0 0.5mm;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 2mm;
      font-size: 10.5px;
      line-height: 1;
      white-space: nowrap;
      overflow: hidden;
    }

    .po {
      flex: 0 0 11mm;
      min-height: 11mm;
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: space-around;
      align-items: flex-start;
      overflow: hidden;
    }

    .border-sig {
      min-width: 38%;
      padding-top: 0.4mm;
      text-align: center;
      font-size: 8.5px;
      line-height: 1;
      overflow: hidden;
    }

    .barcode-image,
    .border-sig img {
      display: block;
      width: auto;
      max-width: 58mm;
      height: 7mm !important;
      margin: 0 auto 0.3mm;
      object-fit: contain;
    }

    .bold {
      font-weight: bold;
    }

    @media print {
      html,
      body {
        width: 210mm !important;
        height: 148mm !important;
        overflow: hidden !important;
      }

      .container {
        page-break-after: avoid;
        break-after: avoid-page;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="header-left">
        <img
          src="data:image/png;base64,${base64Image}"
          alt="Venine logo"
          class="logo-image"
        />
      </div>

      <div class="header-center">
        ${response?.is_status === "1" ? '<div class="bold">REPRINT</div>' : ""}
        <div class="title">ป้ายชี้บ่งผลิตภัณฑ์/วัตถุดิบ</div>
      </div>

      <div class="header-right">
        <!-- 🛠️ แทรก onerror เพื่อให้เรียก direct URL ถ้าโหลดผ่าน Proxy ไม่ได้ -->
        <img
          src="${escapeHtml(qrCodeUrl)}"
          onerror="this.onerror=null;this.src='${escapeHtml(directQrCodeUrl)}';"
          alt="QR Code"
          class="img-qr"
        />
        <div>
          ${escapeHtml(response?.order_number)}
          ${hasValue(response?.materialDoc) ? ` | ${escapeHtml(response.materialDoc)}` : ""}
        </div>
      </div>
    </div>

    <table class="main-table">
      <colgroup>
        <col class="col-left-label" />
        <col class="col-left-value" />
        <col class="col-right-label" />
        <col class="col-right-value" />
      </colgroup>
      <tr>
        <td class="bold">เลขที่สัญญา</td>
        <td colspan="3">${escapeHtml(response?.projectName)}</td>
      </tr>

<tr>
        <td class="bold">ชื่อลูกค้า</td>
        <td class="left-value">${escapeHtml(response?.customerName)}</td>
        <td class="bold">หน่วยงานรับมอบ</td>
        <!-- 🛠️ เพิ่ม style ป้องกันการตกบรรทัด และลดเส้นเหลือ 20 ขีด -->
        <td style="white-space: nowrap; overflow: hidden;">____________________</td>
      </tr>

      <tr>
        <td class="bold">Material Code</td>
        <td class="left-value">${escapeHtml(response?.materialNo)}</td>
        <td colspan="2">${escapeHtml(response?.materialDesc)}</td>
      </tr>

      <tr>
        <td class="bold">สถานะงาน</td>
        <td colspan="3">
          <table class="status-table">
            <tr>
              <td>${selectedStatus === "rawMaterial" ? "☑" : "☐"} วัตถุดิบ</td>
              <td>${selectedStatus === "inProduction" ? "☑" : "☐"} งานระหว่างผลิต</td>
              <td>${selectedStatus === "finishedProduct" ? "☑" : "☐"} สินค้าสำเร็จรูป</td>
              <td>${selectedStatus === "ncr" ? "☑" : "☐"} NCR</td>
              <td>${selectedStatus === "other" ? "☑" : "☐"} อื่นๆ</td>
            </tr>
          </table>
        </td>
      </tr>

      <tr>
        <td class="bold">วันที่รับเข้า/ผลิต</td>
        <td class="left-value">${escapeHtml(response?.postingDate)}</td>
        <td class="bold">ความยาว</td>
        <td>${escapeHtml(lengthText)}</td>
      </tr>

      <tr>
        <td class="bold">หมายเลขภาชนะ</td>
        <td class="left-value">${escapeHtml(response?.returnId)}</td>
        <td class="bold">LOT No.</td>
        <td>${escapeHtml(response?.batchNo)}</td>
      </tr>

      <tr>
        <td class="bold">น้ำหนักรวม</td>
        <td class="left-value">${escapeHtml(totalWeightText)}</td>
        <td class="bold">Size ของวัตถุดิบ</td>
        <td>${escapeHtml(response?.size)}</td>
      </tr>

      <tr>
        <td class="bold">น้ำหนักภาชนะ</td>
        <td class="left-value">${escapeHtml(packagingWeightText)}</td>
        <td class="bold">Grade ของวัตถุดิบ</td>
        <td>${escapeHtml(response?.grade)}</td>
      </tr>

      <tr>
        <td class="bold">น้ำหนักงาน</td>
        <td class="left-value">${escapeHtml(materialWeightText)}</td>
        <td class="bold">เครื่องผลิต</td>
        <td>${escapeHtml(response?.machineNo)}</td>
      </tr>

      <tr>
        <td class="bold">จำนวนสินค้า</td>
        <td class="left-value">${escapeHtml(quantityText)}</td>
        <td class="bold">ชื่อผู้ผลิต</td>
        <td>${escapeHtml(response?.employee_product_name)}</td>
      </tr>

      <tr>
        <td class="bold">Header Text</td>
        <td class="left-value">${escapeHtml(headerText)}</td>
        <td class="bold">หมายเลขรีลลูกค้า</td>
        <td>${escapeHtml(response?.reel_customer)}</td>
      </tr>
    </table>

    <div class="signature">
      <div class="bold">ผู้ส่ง _________________</div>
      <div class="bold">ผู้รับ _________________</div>
      <div class="bold">ผู้ตรวจสอบ _________________</div>
    </div>

    <div class="po">
      ${createBarcodeHtml(response?.po_1, "PO1")}
      ${createBarcodeHtml(response?.po_2, "PO2")}
    </div>
  </div>
</body>
</html>
    `;
  };

  const handlePrint = async () => {
    if (isPrinting) return;

    if (!base64Image || !kanitFontBase64) {
      setModalErrorcheck(true);
      return;
    }

    try {
      setIsPrinting(true);
      const htmlContent = generateHtmlContent();

      if (Platform.OS === "web") {
        await printHtmlOnWeb(htmlContent);
        return;
      }

      // 🛠️ สั่งพิมพ์ HTML ตรงๆ ข้ามการสร้างไฟล์ PDF
      await Print.printAsync({
        html: htmlContent,
      });

    } catch (error: any) {
      console.error("เกิดข้อผิดพลาดในการพิมพ์:", error);
      Alert.alert("🔍 พบสาเหตุแล้ว!", `Error: ${error?.message || String(error)}`);
      setModalPrintcatch(true);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <>
      <View className="flex-1 bg-[#F17736] items-center justify-center">
        <View className="w-full h-[20%] bg-[#F17736] rounded-b-[50px]" />
        <View className="bottom-0 w-full h-[90%] bg-white rounded-t-[20px]" />

        <FooterComponent />

        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: "space-between",
            padding: 15,
          }}
          showsVerticalScrollIndicator={false}
          className="absolute mt-2 top-0 w-[90%] h-[90%] px-2 bg-white rounded-2xl border border-gray-200 shadow-md"
        >
          <View className="w-full items-center justify-center border-b border-gray-300 mb-4">
            <Text className="text-2xl font-kanit py-2.5">
              {t("print_details")}
            </Text>
          </View>

          <View className="flex-1 w-full bg-white">
            <View className="items-center pb-3">
              {response?.is_status === "1" ? (
                <Text className="text-sm text-red-500 font-kanit">
                  สถานะ: พิมพ์ซ้ำ
                </Text>
              ) : null}

              <Text className="text-lg text-gray-700 font-kanit">
                {t("product_label_raw_material")}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">เลขที่สัญญา :</Text>{" "}
                {response?.projectName ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">ชื่อลูกค้า :</Text>{" "}
                {response?.customerName ?? ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">หน่วยงานรับมอบ :</Text>
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">Material Code :</Text>{" "}
                {response?.materialNo ?? ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                {response?.materialDesc ?? ""}
              </Text>
            </View>

            <View className="py-2.5">
              <Text className="text-sm pb-2 text-black font-kanit">
                สถานะงาน
              </Text>

              <View className="flex-row flex-wrap justify-between">
                {jobStatuses.map((item) => (
                  <Pressable
                    key={item.key}
                    onPress={() => setSelectedStatus(item.key)}
                    className="flex-row items-center pr-2"
                  >
                    <Text className="text-sm text-gray-600 font-kanit py-2">
                      {selectedStatus === item.key ? "🔘" : "⚪"} {item.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">วันที่รับเข้า/ผลิต :</Text>{" "}
                {response?.postingDate ?? ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">ความยาว :</Text>{" "}
                {response?.unit === "M"
                  ? `${response?.quantity ?? ""} ${response?.unit ?? ""}`
                  : response?.unit === "ROL" || response?.unit === "DR"
                    ? `${response?.barcode ?? ""} M`
                    : ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">หมายเลขภาชนะ :</Text>{" "}
                {response?.returnId ?? ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">LOT No. :</Text>{" "}
                {response?.batchNo ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">น้ำหนักรวม :</Text>{" "}
                {hasValue(response?.totalWeight)
                  ? `${response.totalWeight} KG`
                  : ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">Size ของวัตถุดิบ :</Text>{" "}
                {response?.size ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">น้ำหนักภาชนะ :</Text>{" "}
                {hasValue(response?.packagingWt)
                  ? `${response.packagingWt} KG`
                  : ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">Grade ของวัตถุดิบ :</Text>{" "}
                {response?.grade ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">น้ำหนักงาน :</Text>{" "}
                {hasValue(response?.materialWt)
                  ? `${response.materialWt} KG`
                  : ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">เครื่องผลิต :</Text>{" "}
                {response?.machineNo ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">จำนวนสินค้า :</Text>{" "}
                {response?.unit === "DR"
                  ? "1 DR"
                  : response?.unit === "M" || response?.unit === "ROL"
                    ? `${response?.quantity ?? ""} ${response?.unit ?? ""}`
                    : hasValue(response?.returnWeight)
                      ? `${response.returnWeight} KG`
                      : ""}
              </Text>

              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">ชื่อผู้ผลิต :</Text>{" "}
                {response?.employee_product_name ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-500 font-kanit">
                <Text className="text-black">หมายเลขรีลลูกค้า :</Text>{" "}
                {response?.reel_customer ?? ""}
              </Text>
            </View>

            <View className="flex-row justify-between py-2.5">
              <Text className="w-1/2 text-sm text-gray-600 font-kanit">
                Header Text
              </Text>
            </View>

            <View className="flex-row justify-between border rounded-lg border-gray-300 py-2.5 mb-2">
              <TextInput
                className="w-full text-sm text-gray-600 font-kanit px-4"
                value={headerText}
                editable={false}
                multiline
                numberOfLines={4}
              />
            </View>
          </View>

          <View className="w-full flex-row items-center justify-between py-2.5 border-t pt-5 border-gray-300">
            <TouchableOpacity
              onPress={() => setModalQuestionVisible(true)}
              className={`w-full bg-[#8badd3] py-2.5 rounded-xl items-center shadow-sm ${
                isPrinting ? "opacity-50" : ""
              }`}
              disabled={isPrinting}
            >
              {isPrinting ? (
                <View className="flex-row items-center">
                  <ActivityIndicator size="small" color="#ffffff" />
                  <Text className="ml-2 text-white text-lg font-kanit">
                    {t("currently_typing")}
                  </Text>
                </View>
              ) : (
                <Text className="text-white text-lg font-kanit">
                  {t("print_label")}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <Modal
        animationType="fade"
        transparent
        visible={modalQuestionVisible}
        onRequestClose={() => setModalQuestionVisible(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="question-circle" size={28} color="#58d68d" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("confirm")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("confirm_print_label")}
            </Text>

            <View className="flex-row justify-around">
              <Pressable
                className="flex-1 bg-gray-300 py-2 rounded-lg mx-1"
                onPress={() => setModalQuestionVisible(false)}
              >
                <Text className="text-center text-gray-700 font-kanit">
                  {t("cancel")}
                </Text>
              </Pressable>

              <Pressable
                className="flex-1 bg-[#58d68d] py-2 rounded-lg mx-1"
                onPress={() => {
                  setModalQuestionVisible(false);
                  void handleSubmit();
                }}
              >
                <Text className="text-center text-white font-kanit">
                  {t("confirm")}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={modalSuccessVisible}
        onRequestClose={() => setModalSuccessVisible(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="check-circle" size={28} color="#58d68d" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("success")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("data_saved_successfully")}
            </Text>

            <Pressable
              className="bg-[#58d68d] py-2 rounded-lg"
              onPress={() => {
                setModalSuccessVisible(false);
                void handlePrint();
              }}
            >
              <Text className="text-center text-white font-kanit">
                {t("confirm")}
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={modalErrorVisible}
        onRequestClose={() => setModalErrorVisible(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="times-circle" size={28} color="#ec7063" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("error")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("unable_to_save_data")}
            </Text>

            <Pressable
              className="bg-[#58d68d] py-2 rounded-lg"
              onPress={() => setModalErrorVisible(false)}
            >
              <Text className="text-center text-white font-kanit">
                {t("confirm")}
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={modalErrorcheck}
        onRequestClose={() => setModalErrorcheck(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="exclamation-triangle" size={28} color="#f39c12" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("error")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("unable_to_load_image_or_font")}
            </Text>

            <Pressable
              className="bg-[#58d68d] py-2 rounded-lg"
              onPress={() => setModalErrorcheck(false)}
            >
              <Text className="text-center text-white font-kanit">
                {t("confirm")}
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={modalPrintcatch}
        onRequestClose={() => setModalPrintcatch(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-4">
          <View className="bg-white rounded-lg p-6 w-full max-w-sm shadow-2xl">
            <View className="flex-row items-center justify-center pb-3">
              <Icon name="exclamation-triangle" size={28} color="#f39c12" />
              <Text className="text-center text-xl font-kanit text-gray-800 pl-2">
                {t("error")}
              </Text>
            </View>

            <Text className="text-center text-gray-600 font-kanit mb-6">
              {t("print_error_occurred")}
            </Text>

            <Pressable
              className="bg-[#58d68d] py-2 rounded-lg"
              onPress={() => setModalPrintcatch(false)}
            >
              <Text className="text-center text-white font-kanit">
                {t("confirm")}
              </Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
}